import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import http from '../../Services/Http';
import Loading from '../../Components/Loading';
import RepBills from './components/RepVotes';
import BackButton from '../../Components/BackButton';
import RepSidebar from './components/RepSidebar';
import { gsap, SplitText, useGSAP, prefersReducedMotion } from '../../lib/gsap';

const RepProfilePage = () => {
   const { repId } = useParams();
   const [rep, setRep] = useState<any>();
   const scope = useRef<HTMLDivElement>(null);

   useEffect(() => {
      http.get(`reps/${repId}`).then(res => setRep(res.data));
   }, [repId]);

   useGSAP(
      () => {
         if (!rep || prefersReducedMotion()) return;
         const split = SplitText.create('[data-rep-name]', {
            type: 'lines,words',
            mask: 'lines',
            autoSplit: true,
            onSplit: self => gsap.from(self.words, { yPercent: 110, stagger: 0.06, duration: 1.3 }),
         });
         gsap.from('[data-rep-portrait]', { clipPath: 'inset(100% 0% 0% 0%)', duration: 1.6, ease: 'expo.inOut' });
         gsap.from('[data-rep-fade]', { autoAlpha: 0, y: 16, stagger: 0.1, delay: 0.5 });
         return () => split.revert();
      },
      { scope, dependencies: [rep] }
   );

   if (!rep) return <Loading label="Retrieving member" />;

   const party = rep.partyHistory?.[0]?.partyAbbreviation;
   const stateCode = rep.terms?.[rep.terms.length - 1]?.stateCode || rep.state;
   const seat = rep.district
      ? `District ${rep.district}, ${rep.state}`
      : rep.title === 'Senator'
      ? `U.S. Senator, ${rep.state}`
      : rep.state || 'At-large';
   const address = rep.addressInformation;

   return (
      <div ref={scope}>
         <header className="page grid grid-cols-1 items-end gap-10 pt-28 md:grid-cols-12">
            <div className="md:col-span-12">
               <BackButton location="/reps" label="All members" />
            </div>
            <div className="md:col-span-4">
               <div data-rep-portrait className="relative aspect-[4/5] max-w-sm overflow-hidden rounded-sm bg-ink-800">
                  {rep.depiction?.imageUrl && (
                     <img
                        src={rep.depiction.imageUrl}
                        alt={rep.depiction.attribution || `${rep.firstName} ${rep.lastName}`}
                        className="h-full w-full object-cover object-top grayscale contrast-110 transition duration-700 hover:grayscale-0"
                     />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-950/80 to-transparent" />
               </div>
            </div>
            <div className="md:col-span-8">
               <p data-rep-fade className="eyebrow mb-5 text-signal-soft">
                  {[party, stateCode].filter(Boolean).join('-') || 'Member of Congress'}
               </p>
               <h1 data-rep-name className="font-display text-6xl leading-[0.9] tracking-tightest md:text-8xl">
                  {`${rep.firstName} ${rep.lastName}`}
               </h1>
               <p data-rep-fade className="mt-6 text-lg text-bone-muted">
                  {seat}
               </p>
               {address && (
                  <div data-rep-fade className="mt-8 flex flex-wrap gap-3">
                     {address.phoneNumber && (
                        <a href={`tel:${address.phoneNumber}`} className="button-ghost">
                           {address.phoneNumber}
                        </a>
                     )}
                     {address.officeAddress && (
                        <a
                           href={`https://www.google.com/maps/search/?q=${encodeURIComponent(address.officeAddress)}`}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="button-ghost"
                        >
                           Office address ↗
                        </a>
                     )}
                  </div>
               )}
            </div>
         </header>

         <div className="page mt-24 grid grid-cols-1 gap-12 lg:grid-cols-12">
            <section className="lg:col-span-8">
               <h2 className="headline">Sponsored legislation</h2>
               <RepBills bioguideId={repId!} />
            </section>
            <div className="lg:col-span-4">
               <div className="lg:sticky lg:top-24">
                  <RepSidebar statements={[]} />
               </div>
            </div>
         </div>
      </div>
   );
};

export default RepProfilePage;
